import React, {forwardRef, useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {makeStyles} from '@material-ui/styles'
import MaterialTable, {MTableBody, MTableToolbar} from 'material-table'
import Typography from '@material-ui/core/Typography'
import SearchIcon from '@material-ui/icons/Search'
import _, {omit} from 'lodash'
import {FormattedMessage} from 'react-intl'
import './DataTable.scss'
import {useSelector} from 'react-redux'
import {getData} from '../../API'
import useTheme from '@material-ui/core/styles/useTheme'
import ActionMenu, {DeleteConfirm} from "./ActionMenu"
import arabic from "./Languages/arabic"
import english from "./Languages/english"
import FilterPanel from "./FilterPanel"
import ColumnDataRender from "./ColumnDataRender"
import AccessGuard from "../AccessGuard"
import {Button, colors as coreColors, fade} from "@material-ui/core"
import AddIcon from "@material-ui/icons/Add"
import {authables} from "../../../utils/globals"
import IconButton from "@material-ui/core/IconButton"
import Badge from "@material-ui/core/Badge"
import TableFooter from "@material-ui/core/TableFooter"
import TableRow from "@material-ui/core/TableRow"
import TableCell from "@material-ui/core/TableCell"
import __ from "../../LanguageHelper"
import * as numeral from "numeral"
import XLSX from 'xlsx'
import moment from "moment"
import {GridLoader} from "react-spinners"
import {chipLabel} from "./ColumnDataRender/ColumnDataRender"
import SortIcon from "@material-ui/icons/Sort"
import SortingPanel from "./SortingPanel"
import DetailsPanel from "../Page/DetailsPanel"

const useStyles = makeStyles((theme) => ({
  stickyActionsColumn: {
    '& table:first-child': {
      '& tr': {
        '& td:last-child, th:last-child': {
          backgroundColor: '#F4F6F8',
          position: 'sticky',
          right: 0,
          width: 50
        },
      }
    }
  },
  footerRow: {
    backgroundColor: theme.palette.primary.main,
    '& *': {
      color: theme.palette.primary.contrastText,
    }
  },
  simpleTable: {
    marginTop: theme.spacing(1)
  },
  actionColumn: {
    display: 'flex',
    alignItems: 'center',
    paddingRight: 15
  },
  toolbarColumn: {
    flex: 1
  },
  toolbarContainer: {
    display: 'flex',
    flexDirection: 'row'
  },
  buttonTitle: {
    marginLeft: 10
  }
}))

const AddFormTrigger = ({editAction, addTitle}) => {
  const classes = useStyles()
  return (
    <Button
      color="secondary"
      variant="contained"
      onClick={() => {
        editAction && editAction()
      }}
    >
      <AddIcon/> {addTitle && <span className={classes.buttonTitle}>{addTitle}</span>}
    </Button>
  )
}

const DataTable = forwardRef((
  {
    endpoint,
    params = {},
    details,
    title,
    columns = [],
    buttons = ['show', 'delete', 'edit'],
    simple,
    withActions,
    editAction,
    protect,
    settings,
    formRef,
    addTitle,
    search,
    summary,
    colors,
    strips,
    limit = 10,
    viewAction,
    onDelete,
    canExport = true,
    pagination = true,
    sortable = false,
    sorterLabel = null,
    inlineSearch,
    inlineSearchWidth,
    ...rest
  },
  ref
) => {
  const classes = useStyles()
  const {token} = useSelector((state) => state.session)

  const theme = useTheme()

  // Table ref
  let tableRef = ref
  if (!ref) {
    tableRef = useRef()
  }

  const [allowSortable] = useState(sortable)
  const [onScreenData, setOnScreenData] = useState({})

  // Delete Confirm
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [recordID, setRecordID] = useState(false)

  const handleClickOpen = () => {
    setShowDeleteConfirm(true)
  }

  const handleClose = () => {
    setShowDeleteConfirm(false)
  }

  // Localization
  const savedDirection = localStorage.getItem('direction')
  let localization = savedDirection === "rtl" ? arabic : english

  const [records, setRecords] = useState({total: 0, current: 0, pages: 1})

  // Format columns
  let formattedColumns = [],
    sumCols = []

  columns.map((column) => {
    column.headerStyle = {whiteSpace: column.wrap ? 'normal' : 'nowrap'}

    if (column.multi) {
      column.filtering = false
    }

    if (column.width) {
      column.headerStyle.width = column.width
    }

    if (column.summary) {
      sumCols.push(`${column.field}:${column.summary}`)
    }

    formattedColumns.push({
      ...column,
      render: rowData => (
        <div style={{minWidth: column.width ? column.width : null, whiteSpace: column.wrap ? 'normal' : 'nowrap'}}>
          <ColumnDataRender
            rowData={rowData}
            column={column}
            endpoint={endpoint}
            tableRef={tableRef}
          />
        </div>
      )
    })
  })

  // Summary
  const [sumColumns] = useState(sumCols)
  const [sumData, setSumData] = useState([])

  let tableColumns = [{
    title: __('م', 'ID'),
    field: 'id',
    filtering: false,
    width: 50,
  }]

  tableColumns = [...tableColumns, ...formattedColumns]

  // Columns object
  let [columnObject, setColumnObject] = useState(tableColumns)

  useEffect(() => {
    if (buttons && buttons.length > 0 && (!simple || withActions)) {
      tableColumns = [...columnObject, ...[{
        filtering: false,
        sorting: false,
        title: __('الإجراء', 'Action'),
        field: 'action',
        render: rowData => (
          <ActionMenu
            id={rowData.id}
            endpoint={endpoint}
            formRef={formRef}
            buttons={buttons}
            editAction={() => {
              editAction && editAction(rowData.id)
            }}
            normalView={!viewAction}
            viewAction={() => {
              viewAction && viewAction(rowData.id)
            }}
            rowData={rowData}
            protect={protect}
            deleteRecord={() => {
              setRecordID(rowData.id)
              setShowDeleteConfirm(true)
            }}/>
        ),
        headerStyle: {textAlign: 'center'},
        width: 150
      }]]
    } else {
      tableColumns = [...columnObject].filter(c => c.field !== 'action')
    }

    setColumnObject(tableColumns)
  }, [withActions])

  // Search drawer
  const [filterOpen, setFilterOpen] = useState(false)

  const toggleFilter = () => {
    setFilterOpen(!filterOpen)
  }

  // Action buttons
  const canSearch = (search && _.isArray(search) && search.length > 0)
  let actions = [{
    isFreeAction: true,
    tooltip: <FormattedMessage id="datatable.reload" defaultMessage="Reload Data"/>,
    icon: 'sync',
    onClick: (evt, data) => {
      tableRef.current.onQueryChange()
    }
  }]

  const [filters, setFilters] = useState([])
  if (!simple && canSearch && !inlineSearch && filters.length === 0) {
    actions.push({
      isFreeAction: true,
      tooltip: <FormattedMessage id="datatable.search" defaultMessage="Search"/>,
      icon: 'search',
      onClick: (evt, data) => {
        toggleFilter()
      }
    })
  }

  // Page size
  let [pageSize, setPageSize] = useState(limit)

  // Filter
  const applyFilter = (filtering) => {
    tableRef.current.onQueryChange({filtering})
    setFilters(filtering)
    // console.log(filtering)
  }

  // Colors
  function calculateColor(data) {
    let color = null
    for (let i = 0; i <= colors.length; i++) {
      if (colors[i]) {
        let col = colors[i],
          value = col.value,
          columnColor = col.color

        let rawData = data[col.field]

        switch (col.compare) {
          case '>':
            color = rawData > value ? columnColor : null
            break
          case '<':
            color = rawData < value ? columnColor : null
            break
          case '=':
            color = rawData === value ? columnColor : null
            break
          case '>=':
            color = rawData >= value ? columnColor : null
            break
          case '<=':
            color = rawData <= value ? columnColor : null
            break
          case '<>':
            color = rawData !== value ? columnColor : null
            break
        }

        if (color) return _.get(coreColors, color)
      }
    }

    return null
  }


  // Export excel
  const [queryParams, setQueryParams] = useState({})
  const [isLoading, setLoading] = useState(false)

  function canBeExported(column) {
    // Excluded types
    const excludedColumnTypes = ['image']
    if (excludedColumnTypes.includes(column.type)) return false

    // Exclude buttons
    if (column.onClick) return false

    // Can be exported
    return true
  }

  const excelExport = async () => {
    setLoading(true)

    if (authables.includes(endpoint)) {
      endpoint = `auth_${endpoint}`
    }

    queryParams.limit = 10000
    queryParams.page = 1

    let result = await getData(endpoint, queryParams, token)
    let data = result.data
    let formattedData = []

    let headers = []
    columns.map(column => {
      if (canBeExported(column)) {
        headers.push(_.isObject(column.title) ? column.title.props.defaultMessage : column.title)
      }
    })

    formattedData.push(headers)

    data.map(item => {
      let itemArray = []
      columns.map(column => {
        if (canBeExported(column)) {
          let columnData = _.get(item, column.field)
          if (column.multi) {
            let regex = /\(([^)]+)\)/g
            let fields = column.field.match(regex),
              fieldOutput = column.field

            if (Array.isArray(fields)) {
              fields.map((field) => {
                let fieldData = _.get(item, field.replace(/[{()}]/g, ''))
                fieldOutput = fieldOutput.replace(field, fieldData)
              })

              columnData = fieldOutput
            }
          }

          if (column.type === "chips" || column.type === "tags") {
            if(_.isArray(columnData)) {
              let fieldOutput = []
              columnData.map((item, index) => {
                fieldOutput.push(chipLabel(column, item))
              })

              columnData = fieldOutput.join(" | ")
            }
          }

          itemArray.push(columnData)
        }
      })

      formattedData.push(itemArray)
    })

    setLoading(false)

    /* convert state to workbook */
    const ws = XLSX.utils.aoa_to_sheet(formattedData)

    _.forEach(ws, (v, c) => {
      if (c !== '!ref') {
        if (headers.indexOf(v.v) >= 0) {
          ws[c]['s'] = {
            fill: {
              patternType: 'solid', // none / solid
              fgColor: {rgb: 'FFD3D3D3'}
            }
          }
        }
      }
    })

    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, "SheetJS")
    /* generate XLSX file and send to client */
    XLSX.writeFile(wb, `exported-data-${moment().format('DD-MM-YYYY HHmmss')}.xlsx`)
  }

  const openSortingPanel = () => {
    if (_.isObject(endpoint)) {
      endpoint = endpoint.post
    }

    DetailsPanel.show({
      modal: true,
      stay: true,
      drawerWidth: 500,
      view: (
        <SortingPanel
          data={onScreenData.data}
          startIndex={onScreenData.startIndex}
          sorterLabel={sorterLabel}
          endpoint={endpoint}
          onLoad={() => {
            tableRef.current.onQueryChange()
            DetailsPanel.hide()
          }}
        />
      )
    })
  }

  return (
    <>
      <AccessGuard permission={'browse'} endpoint={endpoint}>
        {(pagination && !simple) && <Typography variant={"body2"} gutterBottom style={{marginTop: 10}}>
          <FormattedMessage
            id="datatable.founded_records"
            defaultMessage="{total} Records found. Page {current} of {pages}"
            values={records}
          />
        </Typography>}

        {(canSearch && !inlineSearch) && <FilterPanel filterOpen={filterOpen} toggleFilter={toggleFilter} fields={search} applyFilter={applyFilter} filters={filters}/>}

        <DeleteConfirm
          open={showDeleteConfirm}
          handleClose={() => setShowDeleteConfirm(false)}
          tableRef={tableRef}
          endpoint={endpoint}
          id={recordID}
          onDelete={onDelete}
        />

        <div className={simple || buttons.length === 0 ? classes.simpleTable : classes.stickyActionsColumn}>

          <MaterialTable
            isLoading={isLoading}
            tableRef={tableRef}
            title={title}
            columns={columnObject}
            options={{
              filtering: false,
              search: false,
              draggable: false,
              exportButton: canExport && !simple,
              exportCsv: (columns, data) => {
                excelExport(columns, data).finally()
              },
              selection: false,
              headerStyle: {
                fontWeight: 600,
                backgroundColor: '#F4F6F8'
              },
              paging: pagination && !simple,
              pageSize,
              pageSizeOptions: [5, 10, 20, 30, 50],
              rowStyle: rowData => {
                if (strips) {
                  return {
                    backgroundColor: (rowData.tableData.id % 2) ? '#f8fafc' : '#FFF'
                  }
                }

                if (colors) {
                  return {
                    backgroundColor: calculateColor(rowData)
                  }
                }
              },
              ...settings
            }}
            actions={actions}
            data={query => {
              // console.log(query)
              setPageSize(query.pageSize)

              let orderBy = _.get(query, 'orderBy.field')

              return new Promise(async (resolve, reject) => {
                let queryParams = {
                  limit: query.pageSize,
                  page: query.page + 1
                }

                if (query.filters.length > 0) {
                  let search = ''
                  query.filters.map((filter) => {
                    search += `${filter.column.field}:${filter.value};`
                  })

                  queryParams.search = search
                }

                if (query.filtering) {
                  queryParams.filtering = query.filtering
                }

                if (orderBy && !orderBy.includes('.')) {
                  queryParams.orderBy = orderBy || 'id'
                }

                if(!queryParams.orderBy) {
                  queryParams.orderBy = 'id'
                }

                queryParams.sortedBy = query.orderDirection || 'desc'
                queryParams.requestedBy = 'dashboard'

                if (allowSortable && queryParams.orderBy === "id") {
                  queryParams.orderBy = "sorter"
                  queryParams.sortedBy = "asc"
                }

                let paramsKeys = Object.keys(params)
                if (paramsKeys.length > 0) {
                  paramsKeys.map((param) => {
                    queryParams[param] = params[param]
                  })
                }

                if (authables.includes(endpoint)) {
                  endpoint = `auth_${endpoint}`
                }

                // Summary fetch
                if (summary && query.page === 0) {
                  queryParams.summary = sumColumns.join(';')
                  queryParams.endpoint = summary

                  let sumParams = {...queryParams}
                  delete sumParams.limit
                  delete sumParams.orderBy
                  delete sumParams.sortedBy
                  delete sumParams.requestedBy
                  delete sumParams.page

                  getData(`reports`, sumParams, token).then((summaryFetch) => {
                    setSumData(summaryFetch)
                  })
                }

                setQueryParams(queryParams)

                let result = await getData(endpoint, queryParams, token)

                let currentPage = _.get(result, 'meta.pagination.current_page'),
                  totalCount = _.get(result, 'meta.pagination.total'),
                  totalPages = _.get(result, 'meta.pagination.total_pages')

                let data = result ? result.data : []
                data = data.map((item, index) => ({
                  ...item,
                  "#": (currentPage - 1) * query.pageSize + (index + 1)
                }))

                resolve({
                  data,
                  page: result ? currentPage - 1 : 1,
                  totalCount: result ? totalCount : 0
                })

                setRecords({
                  total: result ? totalCount : 0,
                  current: result ? currentPage : 0,
                  pages: result ? totalPages : 0
                })

                setOnScreenData({
                  data,
                  startIndex: (currentPage - 1) * query.pageSize + 1
                })
              })
            }}
            localization={localization.datatable}
            icons={{
              Filter: SearchIcon
            }}
            components={{
              OverlayLoading: props => (
                <div style={{display: 'table', width: '100%', height: '100%', backgroundColor: fade(props.theme.palette.background.paper, 0.7)}}>
                  <div style={{display: 'table-cell', width: '100%', height: '100%', verticalAlign: 'middle', textAlign: 'center'}}>
                    <div style={{display: 'table', margin: "0 auto"}}><GridLoader color={theme.palette.primary.main} size={10}/></div>
                  </div>
                </div>
              ),
              Toolbar: props => (
                <>
                  <div className={classes.toolbarContainer}>
                    {buttons.includes('add') ?
                      <>
                        <div className={classes.toolbarColumn}>
                          <MTableToolbar {...props} />
                        </div>
                        <div className={classes.actionColumn}>
                          <AccessGuard permission={'add'} endpoint={endpoint}>
                            <AddFormTrigger addTitle={addTitle} editAction={() => {
                              editAction && editAction()
                            }}/>
                          </AccessGuard>
                        </div>
                      </>
                      :
                      <div className={classes.toolbarColumn}>
                        <MTableToolbar {...props} />
                      </div>
                    }

                    {(!inlineSearch && filters.length > 0) && <div className={classes.actionColumn}>
                      <IconButton aria-label="search" onClick={toggleFilter}>
                        <Badge color="secondary" badgeContent={filters.length}>
                          <SearchIcon/>
                        </Badge>
                      </IconButton>
                    </div>}

                    {allowSortable && (
                      <div className={classes.actionColumn}>
                        <IconButton
                          aria-label="search"
                          onClick={openSortingPanel}
                        >
                          <SortIcon/>
                        </IconButton>
                      </div>
                    )}

                  </div>

                  {inlineSearch && <div style={{maxWidth: inlineSearchWidth}}>
                    <FilterPanel inline fields={search} applyFilter={applyFilter} filters={filters}/>
                  </div>}
                </>
              ),
              Body: props => (
                <>
                  <MTableBody {...props} />
                  {(summary && Array.isArray(sumData)) && <TableFooter>
                    <TableRow className={classes.footerRow}>
                      {columnObject.map((column, i) => {
                        let colSum = sumData.find(s => s.column === column.field)
                        if (i === 0) return (<TableCell>
                          <Typography variant={"h6"}>
                            {__('إجمالي:', 'Total:')}
                          </Typography>
                        </TableCell>)

                        if (!colSum) return <TableCell/>

                        if (column.type === "money" && colSum.value) {
                          colSum.value = numeral(colSum.value).format('0,0.00')
                        }

                        if (column.summary === "avg" && colSum.value) {
                          colSum.value = numeral(colSum.value).format('0.00')
                        }

                        return (
                          <TableCell>
                            <Typography variant={"body1"} style={{whiteSpace: 'nowrap'}}>
                              {`${column.sum_before || ""} ${colSum.value} ${column.sum_after || ""}`}
                            </Typography>
                          </TableCell>
                        )
                      })}
                    </TableRow>
                  </TableFooter>}
                </>
              )
            }}
            detailPanel={details ? rowData => details(rowData) : null}
            // onRowClick={details ? (event, rowData, togglePanel) => togglePanel() : null}
            {...omit(rest, ['data'])}
          />
        </div>
      </AccessGuard>
    </>
  )
})

DataTable.propTypes = {
  title: PropTypes.any,
  endpoint: PropTypes.string,
  columns: PropTypes.array,
}

export default DataTable
